import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart, PGBarChart, PGGauge } from '../../components/primitives/PGChart';
import {
  GROUP, REGIONS, BRANCHES, TRENDS, MONTHS, ISO_EVENTS,
} from '../../utils/orgData';
import { PG } from '../../utils/theme';
import { useLiveFeed } from '../../components/live/LiveFeedProvider';

const {
  FiDollarSign, FiActivity, FiClock, FiHeart, FiCheckSquare,
  FiArrowUpRight, FiArrowDownRight, FiAlertCircle,
} = FiIcons;

const fmtZAR = (n) => `R ${Math.round(n).toLocaleString('en-ZA')}`;
const fmtZARk = (n) => `R ${Math.round(n / 1000).toLocaleString('en-ZA')}k`;

const severityTone = {
  High: 'alert', Medium: 'warn', Low: 'neutral', Info: 'info',
};

const slaTone = (v) => (v >= 95 ? 'ok' : v >= 90 ? 'warn' : 'alert');

export default function HolisticView() {
  const feed = useLiveFeed();
  const k = GROUP.kpis;

  // Leaderboard: top 5 and bottom 3 by SLA adherence
  const byOps = [...BRANCHES].sort((a, b) => b.ops.slaAdherence - a.ops.slaAdherence);
  const top5 = byOps.slice(0, 5);
  const bottom3 = byOps.slice(-3).reverse();

  // Regional revenue (MTD) bars
  const regionBars = [...REGIONS].sort((a, b) => b.kpis.finance.revenueMTD - a.kpis.finance.revenueMTD);
  const regionCats = regionBars.map((r) => r.name);
  const regionVals = regionBars.map((r) => +(r.kpis.finance.revenueMTD / 1_000_000).toFixed(2));

  return (
    <div className="h-full flex flex-col gap-4 min-h-0">
      {/* Top KPI row */}
      <div className="grid grid-cols-5 gap-4 flex-shrink-0">
        <KPICard
          title="Daily_Revenue"
          value={fmtZARk(k.finance.revenueToday)}
          target={fmtZARk(1_400_000)}
          trend="up"
          trendValue="+4.2%"
          icon={FiDollarSign}
          accent={PG.red}
          animated="ticker"
          numericValue={feed.revenueToday}
          tickerDigits={6}
          tickerPrefix="R "
        />
        <KPICard
          title="Installations_Today"
          value={k.ops.jobsToday}
          unit="jobs"
          target="640"
          trend="up"
          trendValue="+2.8%"
          icon={FiActivity}
          accent={PG.cyan}
          animated="splitflap"
          flapText={String(k.ops.jobsToday).padStart(4, '0')}
          flapWidth={4}
        />
        <KPICard
          title="Avg_Turnaround"
          value={k.ops.avgTurnaroundMin}
          unit="min"
          target="≤50"
          trend={k.ops.avgTurnaroundMin <= 50 ? 'up' : 'down'}
          trendValue={k.ops.avgTurnaroundMin <= 50 ? '−3m' : '+4m'}
          icon={FiClock}
          accent={PG.amber}
          sparkValues={feed.wipAgeP90Mins}
        />
        <KPICard
          title="Group_NPS"
          value={k.cx.nps}
          unit="/10"
          target="9.0"
          trend="up"
          trendValue="+0.3"
          icon={FiHeart}
          accent={PG.emerald}
        />
        <KPICard
          title="ISO_Compliance"
          value={`${k.iso.complianceScore}%`}
          target="≥99%"
          trend="up"
          trendValue="+0.4%"
          icon={FiCheckSquare}
          accent={PG.violet}
        />
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* Left 8/12 */}
        <div className="col-span-8 flex flex-col gap-4 min-h-0">
          <Panel
            title="Regional_Performance"
            subtitle="Revenue MTD // ZAR millions"
            right={<StatusPill tone="info">Live</StatusPill>}
            className="flex-1 min-h-0"
          >
            <div className="h-full p-2">
              <PGBarChart
                categories={regionCats}
                series={[{
                  name: 'Revenue MTD',
                  data: regionVals.map((v) => ({
                    value: v,
                    itemStyle: {
                      color: v >= 10 ? PG.emerald : v >= 5 ? PG.amber : PG.red,
                    },
                  })),
                }]}
                yFormatter={(v) => `R${v}M`}
              />
            </div>
          </Panel>

          <Panel
            title="12_Month_Group_Revenue"
            subtitle="Rolling 12 months // ZAR"
            right={<StatusPill tone="ok">Trending_Up</StatusPill>}
            className="flex-1 min-h-0"
            livePulse
          >
            <div className="h-full p-2">
              <PGLineChart
                categories={MONTHS}
                series={[{
                  name: 'Group Revenue',
                  data: TRENDS.groupRevenue.map((v) => +(v / 1_000_000).toFixed(2)),
                  lineStyle: { color: PG.red, width: 2 },
                  itemStyle: { color: PG.red },
                }]}
                yFormatter={(v) => `R${v}M`}
              />
            </div>
          </Panel>
        </div>

        {/* Right 4/12 */}
        <div className="col-span-4 flex flex-col gap-4 min-h-0">
          <Panel
            title="SLA_Adherence"
            subtitle="Group // Target 95%"
            className="flex-shrink-0"
          >
            <div className="h-52">
              <PGGauge value={+k.ops.slaAdherence.toFixed(1)} label="SLA" />
            </div>
          </Panel>

          <Panel
            title="Branch_Leaderboard"
            subtitle="By SLA adherence"
            className="flex-1 min-h-0"
          >
            <div className="h-full overflow-y-auto custom-scrollbar p-3 space-y-1">
              <div className="font-mono-tech text-[9px] uppercase tracking-[0.25em] text-[#00C27A] mb-1 flex items-center gap-2">
                <FiArrowUpRight /> Top_5
              </div>
              {top5.map((b, i) => (
                <Link
                  key={b.id}
                  to={`/sliver/holistic/branch/${b.id}`}
                  className="flex items-center justify-between px-2 py-2 border border-white/5 hover:border-[#D71921] hover:bg-white/5 transition-all group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono-tech text-[10px] text-neutral-600 w-4">{String(i + 1).padStart(2, '0')}</span>
                    <span className="font-mono-tech text-[10px] uppercase tracking-wider text-white truncate">{b.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-doto text-sm text-[#00C27A]">{b.ops.slaAdherence}%</span>
                  </div>
                </Link>
              ))}

              <div className="font-mono-tech text-[9px] uppercase tracking-[0.25em] text-[#D71921] mt-3 mb-1 flex items-center gap-2">
                <FiArrowDownRight /> Bottom_3
              </div>
              {bottom3.map((b, i) => (
                <Link
                  key={b.id}
                  to={`/sliver/holistic/branch/${b.id}`}
                  className="flex items-center justify-between px-2 py-2 border border-[#D71921]/30 hover:border-[#D71921] hover:bg-[#D71921]/10 transition-all"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono-tech text-[10px] text-neutral-600 w-4">{String(i + 1).padStart(2, '0')}</span>
                    <span className="font-mono-tech text-[10px] uppercase tracking-wider text-white truncate">{b.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-doto text-sm text-[#D71921]">{b.ops.slaAdherence}%</span>
                  </div>
                </Link>
              ))}
            </div>
          </Panel>

          <Panel
            title="Digital_ISO_Status"
            subtitle={`Compliance ${k.iso.complianceScore}% // ${k.iso.ncrOpen} open NCR`}
            className="flex-1 min-h-0"
          >
            <div className="h-full overflow-y-auto custom-scrollbar p-3 space-y-2">
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="border border-white/5 p-2">
                  <div className="font-mono-tech text-[8px] uppercase tracking-widest text-neutral-500">NCR_Open</div>
                  <div className="font-doto text-xl text-white">{k.iso.ncrOpen}</div>
                </div>
                <div className="border border-white/5 p-2">
                  <div className="font-mono-tech text-[8px] uppercase tracking-widest text-neutral-500">CAPA_Eff</div>
                  <div className="font-doto text-xl text-[#00C27A]">{k.iso.capaEffectiveness}%</div>
                </div>
                <div className="border border-white/5 p-2">
                  <div className="font-mono-tech text-[8px] uppercase tracking-widest text-neutral-500">Audit_Cov</div>
                  <div className="font-doto text-xl text-[#00D4FF]">{k.iso.auditCoverage}%</div>
                </div>
              </div>
              {ISO_EVENTS.slice(0, 4).map((e) => (
                <div key={e.id} className="flex items-start justify-between gap-2 border border-white/5 p-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">{e.id}</span>
                      <span className="font-mono-tech text-[9px] text-neutral-600">// {e.branch}</span>
                    </div>
                    <div className="font-mono-tech text-[10px] text-white truncate">{e.title}</div>
                  </div>
                  <StatusPill tone={severityTone[e.severity] || 'neutral'}>{e.severity}</StatusPill>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
